import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from 'utils/deepParseJson'

/**
 * Single source of truth for where auth tokens live.
 *
 * Historically the access token was read from two places with slightly different
 * logic in each caller (redux-persist state and localStorage.access_token).
 * Everything now goes through here so there is one answer to "what is the current
 * token", and one place to change if storage ever moves.
 *
 * Storage choice (see docs/AUTH-SESSION.md): the refresh token is kept in
 * localStorage next to the access token rather than an httpOnly cookie. That is a
 * deliberate trade-off for this codebase — a cookie would need cross-site
 * SameSite=None;Secure handling between app.* and api.* plus CORS credential
 * changes. It is compensated by a short refresh lifetime, rotation on every use,
 * server-side reuse detection and revocation on logout. Moving to an httpOnly
 * cookie remains the stronger option and is tracked as remaining work.
 */

export const ACCESS_TOKEN_KEY = 'access_token'
export const REFRESH_TOKEN_KEY = 'refresh_token'

const safeGet = (key) => {
    try {
        return localStorage.getItem(key) || ''
    } catch (e) {
        return ''
    }
}

const safeSet = (key, value) => {
    try {
        if (value) {
            localStorage.setItem(key, value)
        } else {
            localStorage.removeItem(key)
        }
    } catch (e) {
        /* private mode / storage disabled — the in-memory redux copy still works */
    }
}

/**
 * The access token. Prefers redux-persist (the source of truth once the store has
 * rehydrated) and falls back to the raw key so a reload mid-rehydration still
 * carries a token.
 */
export const getAccessToken = () => {
    try {
        const persisted = deepParseJson(localStorage.getItem(PERSIST_STORE_NAME))
        const fromStore = persisted?.auth?.session?.token
        if (fromStore) return fromStore
    } catch (e) {
        /* fall through to the raw key */
    }
    return safeGet(ACCESS_TOKEN_KEY)
}

export const setAccessToken = (token) => safeSet(ACCESS_TOKEN_KEY, token)

export const getRefreshToken = () => safeGet(REFRESH_TOKEN_KEY)

export const setRefreshToken = (token) => safeSet(REFRESH_TOKEN_KEY, token)

/**
 * Clears every trace of the session. Used by sign-out and by a failed refresh.
 * `login_time` is removed too: it backed the old 12-hour client-side session
 * timer, which no longer exists (the server decides when a session ends).
 */
export const clearAuthTokens = () => {
    safeSet(ACCESS_TOKEN_KEY, null)
    safeSet(REFRESH_TOKEN_KEY, null)
    try {
        localStorage.removeItem('userDetails')
        localStorage.removeItem('login_time')
    } catch (e) {
        /* ignore */
    }
}
